import { config, storageKey } from "../config";
import { ensureDeviceAccount, clientContext } from "../members/device-session";
import { prepareMeta, startMetaSession, captureMeta, deliverMeta, resetMetaSession, type MetaCandidate } from "./meta";

export type AnalyticsEvent = "install_prompt" | "install_reported" | "video_open" | "frame_loaded" | "video_play" | "demo_play" | "demo_pause" | "demo_ended";
type Session = { sessionId: string; visitorId: string; instanceId: string; identityToken: string; environment: string; source?: string };
const identityKey = storageKey("anonymous-identity");
let initialized: Promise<Session | null> | null = null;
let current: Session | null = null;
let flushing = false;
let retry: ReturnType<typeof setTimeout> | undefined;
let failures = 0;
const pending: { id: string; type: AnalyticsEvent; meta?: MetaCandidate }[] = [];
const uuid = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
let launchId = uuid();
export function resetAnalytics() { initialized = null; current = null; pending.length = 0; launchId = uuid(); failures = 0; clearTimeout(retry); resetMetaSession(); }
const storedToken = () => { try { return localStorage.getItem(identityKey); } catch { return null; } };
const saveToken = (token: string | null) => {
  try { if (token) localStorage.setItem(identityKey, token); else localStorage.removeItem(identityKey); } catch { /* Private browsing can refuse storage. */ }
};

async function request(url: string, options: RequestInit) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try { return await fetch(url, { ...options, signal: controller.signal }); }
  finally { clearTimeout(timeout); }
}

async function register(reset = false): Promise<Session> {
  await ensureDeviceAccount();
  await prepareMeta();
  const params = new URLSearchParams(location.search);
  const attribution: Record<string, string> = {};
  for (const key of ["source", "medium", "campaign", "content", "term"]) {
    const value = params.get(`utm_${key}`);
    if (value) attribution[key] = value.replace(/[\x00-\x1f\x7f]/g, "").slice(0, 100);
  }
  for (const key of ["fbclid", "gclid", "ttclid", "msclkid"]) {
    const value = params.get(key);
    if (value) attribution[key] = value.replace(/[\x00-\x1f\x7f]/g, "").slice(0, 400);
  }
  const response = await request(`${config.apiBaseUrl}/v1/pwa/sessions`, {
    method: "POST", cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      identityToken: reset ? null : storedToken(), launchId,
      ...clientContext(),
      appVersion: config.version, attribution,
    }),
  });
  if (response.status === 401 && !reset) { saveToken(null); return register(true); }
  if (!response.ok) throw new Error("Analytics session unavailable");
  const session = await response.json() as Session;
  if (session.environment !== config.environment) throw new Error("Analytics environment mismatch");
  saveToken(session.identityToken);
  current = session;
  startMetaSession({ ...session, displayMode: clientContext().displayMode });
  return session;
}

export function initializeAnalytics() {
  if (window.top !== window.self) return Promise.resolve(null);
  if (!initialized) initialized = register().catch(() => { initialized = null; return null; });
  return initialized;
}

async function flush() {
  if (flushing || !pending.length) return;
  flushing = true;
  try {
    const session = current || await initializeAnalytics();
    if (!session) throw new Error("No analytics session");
    const batch = pending.slice(0, 20);
    const response = await request(`${config.apiBaseUrl}/v1/pwa/events`, {
      method: "POST", keepalive: true, cache: "no-store",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.identityToken}` },
      body: JSON.stringify({ sessionId: session.sessionId, events: batch.map(({ id, type }) => ({ id, type })) }),
    });
    if (!response.ok) throw new Error("Analytics delivery unavailable");
    batch.forEach(event => deliverMeta(event.meta));
    pending.splice(0, batch.length);
    failures = 0;
  } catch { failures++; }
  finally {
    flushing = false;
    if (pending.length && failures < 4) {
      clearTimeout(retry);
      retry = setTimeout(() => void flush(), failures ? 2000 * failures : 0);
    }
  }
}

export function track(type: AnalyticsEvent) {
  if (pending.length >= 100) return;
  const id = uuid();
  pending.push({ id, type, meta: captureMeta(type, id) });
  void flush();
}

addEventListener("online", () => { failures = 0; void flush(); });
addEventListener("pagehide", () => void flush());
